import { useState } from 'react'

import { generateTypography, MaterialIcon, MtTypography, TypographyTokenValue } from '@makyoapp/makyo-tw-ui'

import { BrandKitFont } from './BrandKitFont'

const PreviewAccordion = ({
  title,
  children,
  defaultOpen = false,
  preview,
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
  preview?: React.ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className='mb-4 border bdr rounded-md overflow-hidden bg-zinc-200 dark:bg-slate-900'>
      <div
        className='flex justify-between items-center p-4 cursor-pointer mk-bg-hover'
        onClick={() => setIsOpen(!isOpen)}
      >
        <h2 className='text-m-l font-semibold capitalize flex items-center gap-2'>
          {title}
          {preview && <span className='ml-2'>{preview}</span>}
        </h2>
        <span className='material-symbols-rounded'>{isOpen ? 'expand_less' : 'expand_more'}</span>
      </div>
      {isOpen && <div className='p-4'>{children}</div>}
    </div>
  )
}

const TypographyItem = ({ tokenValue }: { tokenValue: TypographyTokenValue }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(tokenValue.style.font)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className='mk-bg border bdr rounded-md p-4 cursor-pointer relative' onClick={handleCopy}>
      <div className='flex justify-between items-start mb-2'>
        <div className='font-mono text-[var(--color-on-surface)]/80'>{tokenValue.key}</div>
        {copied && (
          <div className='absolute top-2 right-2 bg-black text-white text-xs py-1 px-2 rounded shadow-md'>Copied!</div>
        )}
      </div>

      <div style={tokenValue.style} className='mb-3'>
        The quick brown fox jumps over the lazy dog
      </div>

      <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[var(--color-on-surface)]/80'>
        <div>
          Font: <span className='font-semibold'>{tokenValue.fontFamily}</span>
        </div>
        <div>
          Weight: <span className='font-semibold'>{tokenValue.fontWeight}</span>
        </div>
        <div>
          Size: <span className='font-semibold'>{tokenValue.fontSize}</span>
        </div>
        <div>
          Line Height: <span className='font-semibold'>{tokenValue.lineHeight}</span>
        </div>
        <div>
          Letter Spacing: <span className='font-semibold'>{tokenValue.letterSpacing}</span>
        </div>
        <div>
          Transform: <span className='font-semibold'>{tokenValue.textTransform}</span>
        </div>
      </div>
    </div>
  )
}

export interface TypographyDemoProps {
  typography: MtTypography
}

export const TypographyDemo = ({ typography }: TypographyDemoProps) => {
  const typographyTokens = generateTypography(typography)

  // Group tokens by category
  const categories = {
    display: Object.values(typographyTokens).filter((token) => token.key.startsWith('display')),
    headline: Object.values(typographyTokens).filter((token) => token.key.startsWith('headline')),
    title: Object.values(typographyTokens).filter((token) => token.key.startsWith('title')),
    label: Object.values(typographyTokens).filter((token) => token.key.startsWith('label')),
    body: Object.values(typographyTokens).filter((token) => token.key.startsWith('body')),
    navigation: Object.values(typographyTokens).filter((token) => token.key.startsWith('navigationbar')),
    html: Object.values(typographyTokens).filter((token) => token.key.startsWith('html')),
  }

  // Create preview samples for each category
  const previews = {
    display: <span style={typographyTokens['display-medium']?.style}>Aa</span>,
    headline: <span style={typographyTokens['headline-medium']?.style}>Aa</span>,
    title: <span style={typographyTokens['title-medium']?.style}>Aa</span>,
    label: <span style={typographyTokens['label-medium']?.style}>Aa</span>,
    body: <span style={typographyTokens['body-medium']?.style}>Aa</span>,
    navigation: <span style={typographyTokens['navigationbar-tablet']?.style}>Aa</span>,
    html: <span style={typographyTokens['html-paragraph']?.style}>Aa</span>,
  }

  return (
    <div className='h-full'>
      <header className='flex justify-between items-center'>
        <div className='flex items-center gap-4'>
          <div className='mr-4'>
            <MaterialIcon icon='text_fields' size={40} />
          </div>
          <div>
            <div className='text-m-l font-bold'>Typography System</div>
            <p className='mt-1'>
              Typography tokens available in the brand kit. Click on any category to expand and see the tokens
            </p>
          </div>
        </div>

        <div className='pr-4'>
          <BrandKitFont />
        </div>
      </header>

      <hr className='my-6 bdr' />

      {Object.entries(categories).map(([category, tokens]) => (
        <PreviewAccordion
          key={category}
          title={`${category}`}
          defaultOpen={category === 'display'}
          preview={previews[category as keyof typeof previews]}
        >
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {tokens.map((tokenValue) => (
              <TypographyItem key={tokenValue.key} tokenValue={tokenValue} />
            ))}
          </div>
        </PreviewAccordion>
      ))}

      <hr className='my-6 bdr' />

      <PreviewAccordion title='Examples' defaultOpen={true}>
        <div className='space-y-4 p-6 bg-[var(--color-surface)] border bdr rounded-md'>
          <h1 style={typographyTokens['display-large'].style}>Display Large Heading</h1>
          <h2 style={typographyTokens['headline-large'].style}>Headline Large Subheading</h2>
          <h3 style={typographyTokens['title-large'].style}>Title Large Section</h3>
          <p style={typographyTokens['body-large'].style}>
            This is body large text. The quick brown fox jumps over the lazy dog. This demonstrates how typography
            tokens can be used in your application.
          </p>
          <div style={typographyTokens['label-medium'].style}>LABEL MEDIUM FOR UI ELEMENTS</div>
          <p style={typographyTokens['body-small'].style}>
            This is body small text, often used for captions or secondary information.
          </p>
        </div>
      </PreviewAccordion>
    </div>
  )
}
